import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { BaseCustomEditFieldComponent } from '../../../../base/form';
import { BehaviorSubject, defer, of, Subscription } from 'rxjs';
import {
  arr_count,
  arr_filter,
  arr_map,
  arr_reduce,
  blank,
  data_get,
  fallback,
  filled,
  format_datetime,
  generateId,
  map_to_arr,
  observe,
  OptionalValue,
  to_local_datetime,
  transform,
} from '../../../../shared/utils/common';
import { ApiService } from '../../../../services/api.service';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { FormService, StudioFormTab } from '../../../../services/form.service';
import { get_studio_fields_info } from '../../../../shared/utils/studio';
import { FieldmagicDropdownOption } from '../../../../shared/components/forms/input/dropdown/dropdown.component';
import { AssetTypeFaultSeverity } from '../../../asset-types';
import { AssetActualFault } from '../../types';
import { DIALOG_DATA, DialogService } from '../../../../services/dialog.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'assets-actual-faults',
  template: `
    <div
      *ngIf="possibilities$ | async as possibilities"
      class="d-flex flex-column"
      [class.d-flex-gap-2x]="possibilities | filled"
    >
      <div
        fieldmagicGridRow
      >
        <div
          *ngIf="possibilities | filled"
          class="d-flex d-flex-gap justify-content-between w-100 align-items-center"
        >
          <div fieldmagicGridColumn="6">
            <fieldmagic-dropdown-input
              class="w-100"
              [(ngModel)]="possibility"
              [options]="possibilities$ | async"
              [grouped]="true"
              placeholder="Please select a defect to add..."
              (ngModelChange)="onPossibilityChange()"
              [standalone]="true"
            >
              <ng-template
                fieldmagicDropdownOptionGroupTmp
                let-item="item"
              >
                <div
                  class="w-100 p-2"
                  [style.background]="item | data_get: 'first.color'"
                >
                  <span
                    [style.color]="item | data_get: 'first.color' | bg_text_color"
                  >
                    {{ item.text | translate }}
                  </span>
                </div>
              </ng-template>
            </fieldmagic-dropdown-input>
          </div>

          <div
            fieldmagicGridColumn="6"
            class="d-flex justify-content-end"
          >
            <fieldmagic-primary-button
              label="Add Fault"
              [icon]="[
                {
                  icon: 'plus-circle',
                  klass: 'text-success'
                }
              ]"
              purpose="icon"
              (click)="onAddFault()"
            >
            </fieldmagic-primary-button>
          </div>
        </div>

        <div
          *ngIf="possibilities | blank"
          fieldmagicGridColumn="12"
          class="d-flex d-flex-gap justify-content-end"
        >
          <fieldmagic-primary-button
            label="Add Fault"
            [icon]="[
              {
                icon: 'plus-circle',
                klass: 'text-success'
              }
            ]"
            purpose="icon"
            (click)="onAddFault()"
          >
          </fieldmagic-primary-button>
        </div>
      </div>

      <div
        fieldmagicGridRow
      >
        <div
          fieldmagicGridColumn="12"
        >
          <table class="table table-bordered">
            <thead>
              <tr>
                <td width="12%">
                  <fieldmagic-text
                    content="Severity"
                    purpose="table-header"
                    [withRequiredMarker]="true"
                  >
                  </fieldmagic-text>
                </td>
                <td width="40%">
                  <fieldmagic-text
                    content="Defect"
                    purpose="table-header"
                    [withRequiredMarker]="true"
                  >
                  </fieldmagic-text>
                </td>
                <td width="40%">
                  <fieldmagic-text
                    content="Comments"
                    purpose="table-header"
                  >
                  </fieldmagic-text>
                </td>
                <td width="5%">
                  <div class="d-flex d-flex-gap align-items-center justify-content-center">
                    <fieldmagic-text
                      content="Acknowledged?"
                      purpose="table-header"
                    >
                    </fieldmagic-text>

                    <fieldmagic-checkbox-input
                      [useNative]="true"
                      [(ngModel)]="acknowledgedAll"
                      tooltip="Click to mark/unmark all as acknowledged"
                      (ngModelChange)="onAcknowledgedAll($event)"
                    >
                    </fieldmagic-checkbox-input>
                  </div>
                </td>
                <td width="5%">
                  <div class="d-flex d-flex-gap align-items-center justify-content-center">
                    <fieldmagic-text
                      content="Resolved?"
                      purpose="table-header"
                    >
                    </fieldmagic-text>

                    <fieldmagic-checkbox-input
                      [useNative]="true"
                      [(ngModel)]="resolvedAll"
                      tooltip="Click to mark/unmark all as resolved"
                      (ngModelChange)="onResolvedAll($event)"
                    >
                    </fieldmagic-checkbox-input>
                  </div>
                </td>
                <td width="5%">
                  <fieldmagic-text
                    content="Photo"
                    purpose="table-header"
                  >
                  </fieldmagic-text>
                </td>
                <td width="5%">
                </td>
              </tr>
            </thead>

            <tbody>
              <ng-container *ngIf="fetching$ | async">
                <tr class="text-center">
                  <td colspan="7">
                    <fieldmagic-icon [spin]="true"></fieldmagic-icon>
                  </td>
                </tr>
              </ng-container>

              <ng-container *ngIf="! (fetching$ | async)">
                <ng-container *ngIf="faults$ | async as faults">
                  <tr
                    *ngIf="isEmpty"
                    class="text-center"
                  >
                    <td colspan="7">No defects recorded for this asset.</td>
                  </tr>

                  <ng-container *ngFor="let fault of faults; let fault_index = index;">
                    <ng-container *ngIf="! fault.deleted">
                      <tr>
                        <td
                          *ngIf="fault.asset_type_severity_id | filled"
                          [style.background]="fault.color"
                          [style.color]="fault.color | bg_text_color"
                        >
                          {{ fault.severity }}
                        </td>

                        <td
                          *ngIf="fault.asset_type_severity_id | blank"
                        >
                          <div class="d-flex d-flex-gap align-items-center">
                            <fieldmagic-color-picker-input
                              [displayOnly]="true"
                              [displayValue]="false"
                              [standalone]="true"
                              [(ngModel)]="fault.color"
                            >
                            </fieldmagic-color-picker-input>

                            <fieldmagic-dropdown-input
                              class="w-100"
                              [options]="severities$ | async"
                              [(ngModel)]="fault.dropdown_key"
                              bindValue="dropdown_key"
                              (ngModelChange)="onSeverityChange(fault_index, $event)"
                            >
                            </fieldmagic-dropdown-input>
                          </div>
                        </td>


                        <td *ngIf="fault.asset_type_severity_id | filled">
                          <span>{{ fault.fault }}</span>
                        </td>

                        <td *ngIf="fault.asset_type_severity_id | blank">
                          <fieldmagic-textarea-input
                            [standalone]="true"
                            [expandable]="true"
                            [(ngModel)]="fault.fault"
                            (ngModelChange)="emitChanged()"
                            placeholder="Please tell us about the fault."
                          >
                          </fieldmagic-textarea-input>
                        </td>

                        <td>
                          <fieldmagic-textarea-input
                            [standalone]="true"
                            [(ngModel)]="fault.comments"
                            placeholder="Comments"
                            [expandable]="true"
                            (ngModelChange)="emitChanged()"
                          >
                          </fieldmagic-textarea-input>
                        </td>
                        <td class="text-center">
                          <div class="d-flex justify-content-center align-items-center w-100 h-100">
                            <fieldmagic-checkbox-input
                              [useNative]="true"
                              [(ngModel)]="fault.acknowledged"
                              tooltip="Mark as acknowledged"
                              (ngModelChange)="emitChanged()"
                            >
                            </fieldmagic-checkbox-input>
                          </div>
                        </td>
                        <td>
                          <div class="d-flex d-flex-gap justify-content-center align-items-center w-100 h-100">
                            <fieldmagic-checkbox-input
                              [useNative]="true"
                              [(ngModel)]="fault.resolved"
                              tooltip="Mark as resolved"
                              (ngModelChange)="emitChanged()"
                            >
                            </fieldmagic-checkbox-input>

                            <fieldmagic-icon
                              *ngIf="fault.resolved"
                              [icon]="[{
                                icon: 'info-circle',
                                klass: 'text-info'
                              }]"
                              tooltip="Click here to add resolution notes"
                              (click)="onViewResolutionNotes(fault_index)"
                            >
                            </fieldmagic-icon>
                          </div>
                        </td>
                        <td>
                          <div class="d-flex d-flex-gap justify-content-center align-items-center w-100 h-100">
                            <ng-container
                              *ngIf="fault.image_path | filled"
                            >
                              <a
                                href="{{ fault.image_url }}"
                                target="_blank"
                                matTooltip="{{ 'View Photo' | translate }}"
                                class="btn btn-link"
                              >
                                <fieldmagic-icon
                                  icon="file-image"
                                >
                                </fieldmagic-icon>
                              </a>

                              <span>/</span>

                              <fieldmagic-primary-button
                                purpose="delete-icon"
                                tooltip="Remove Photo"
                                (click)="onImageRemove(fault_index)"
                              >
                              </fieldmagic-primary-button>
                            </ng-container>

                            <ng-container
                              *ngIf="fault.image_path | blank"
                            >
                              <span>--</span>
                            </ng-container>
                          </div>
                        </td>
                        <td>
                          <div class="d-flex justify-content-center align-items-center w-100 h-100">
                            <fieldmagic-primary-button
                              purpose="delete-icon"
                              (click)="onFaultRemove(fault_index)"
                            >
                            </fieldmagic-primary-button>
                          </div>
                        </td>
                      </tr>
                    </ng-container>
                  </ng-container>
                </ng-container>
              </ng-container>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `,
  providers: [
    DialogService,
  ],
})
export class AssetActualFaultInputComponent extends BaseCustomEditFieldComponent<AssetActualFaultInputData[]>
  implements OnInit, OnDestroy {
  possibility?: OptionalValue<AssetSeverityFaultOption>;
  acknowledgedAll: boolean = false;
  resolvedAll: boolean = false;

  readonly fetching$ = new BehaviorSubject<boolean>(true);

  readonly faults$ = new BehaviorSubject<AssetActualFaultInputData[]>([]);
  readonly possibilities$ = new BehaviorSubject<AssetSeverityFaultOption[]>([]);
  readonly severities$ = new BehaviorSubject<AssetSeverityLevelOption[]>([]);

  private readonly _assetTypeId$ = new BehaviorSubject<OptionalValue<string>>(null);

  get isEmpty(): boolean {
    const faults = this.faults$.value;

    return blank(arr_filter(faults, {
      predicate: (fault) => !fault.deleted,
    }));
  }

  private _onFetchingFaultsSubscription?: Subscription;
  private _fetchingPossibilitiesSubscription?: Subscription;
  private _onFaultsChangedSubscription?: Subscription;
  private _onAssetTypeChangeSubscription?: Subscription;
  private _onFetchingSeverityOptionsSubscription?: Subscription;

  constructor(
    private readonly _api: ApiService,
    private readonly _forms: FormService,
    private readonly _dialog: DialogService,
    private readonly _translator: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this._listenToFieldChanges();
    this._fetchGlobalSeverities();

    this._fetchingPossibilitiesSubscription = this._fetchPossibilities();

    if (this.mode$.value == 'edit') {
      this._onFetchingFaultsSubscription = observe({
        before: () => this.fetching$.next(true),
        after: () => this.fetching$.next(false),
        observable: () => this._api.callPrivate$({
          path: '/assets/faults',
          action: 'post',
          expectsNewFormat: true,
          body: {
            'asset_id': this.data$.value.record_id,
          },
          onSuccessResponse: (response) => fallback(response.get('items'), {
            fallback: () => [],
          }),
        }),
      }).subscribe((faults: AssetActualFault[]) => {
        const transformed = arr_map(faults, {
          transformer: (fault) => {
            return {
              id: fault.id,
              fault: fault.fault,
              acknowledged: fallback(data_get(fault, 'acknowledged'), {
                fallback: () => false,
              }),
              resolved: fallback(data_get(fault, 'resolved'), {
                fallback: () => false,
              }),
              comments: data_get(fault, 'comments'),
              asset_type_fault_id: data_get(fault, 'asset_type_severity_fault_id'),
              asset_type_severity_id: data_get(fault, 'asset_type_fault_severity_id'),
              resolution_notes: data_get(fault, 'resolution_notes'),
              color: data_get(fault, 'color'),
              severity: data_get(fault, 'severity'),
              date_resolved: data_get(fault, 'date_resolved'),
              severity_level: fallback(data_get(fault, 'severity_level'), {
                fallback: () => 0,
              }),
              dropdown_key: data_get(fault, 'dropdown_key'),
              job_id: data_get(fault, 'job_id'),
              image_path: data_get(fault, 'image_path'),
              image_url: data_get(fault, 'image_url'),
            };
          },
        });

        if (filled(transformed)) {
          const acknowledged = arr_filter(transformed, {
            predicate: (fault) => fault.acknowledged,
          });

          const resolved = arr_filter(transformed, {
            predicate: (fault) => fault.resolved,
          });

          if (arr_count(acknowledged) == arr_count(transformed)) {
            this.acknowledgedAll = true;
          }

          if (arr_count(resolved) == arr_count(transformed)) {
            this.resolvedAll = true;
          }
        }

        this.faults$.next(transformed);
      });
    } else {
      this.fetching$.next(false);
    }

    this._onFaultsChangedSubscription = this.faults$.subscribe((faults) => this.parent$.value.setValue({
      actual_faults: arr_map(faults, {
        transformer: (fault) => ({
          ...fault,
        }),
      }),
    }));
  }

  ngOnDestroy(): void {
    if (this._onFetchingFaultsSubscription) {
      this._onFetchingFaultsSubscription.unsubscribe();
    }

    if (this._onFaultsChangedSubscription) {
      this._onFaultsChangedSubscription.unsubscribe();
    }

    if (this._onAssetTypeChangeSubscription) {
      this._onAssetTypeChangeSubscription.unsubscribe();
    }

    if (this._fetchingPossibilitiesSubscription) {
      this._fetchingPossibilitiesSubscription.unsubscribe();
    }

    if (this._onFetchingSeverityOptionsSubscription) {
      this._onFetchingSeverityOptionsSubscription.unsubscribe();
    }
  }

  onAddFault(): void {
    const current = this.faults$.value;

    current.push({
      id: generateId(),
      fault: '',
      resolved: false,
      resolution_notes: null,
      comments: null,
      acknowledged: false,
      dropdown_key: null,
      severity: null,
      severity_level: 0,
    });

    this.faults$.next(current);
  }

  onFaultRemove(index: number): void {
    const current = this.faults$.value;

    if (current.length < index) {
      return;
    }

    if (index > current.length) {
      return;
    }

    current[index] = {
      ...current[index],
      deleted: true,
    };

    this.faults$.next(current);
  }

  onPossibilityChange(): void {
    const selected = this.possibility;

    if (blank(selected)) {
      return;
    }

    this.faults$.next([
      ... this.faults$.value,
      ...[{
        id: generateId(),
        asset_type_severity_id: selected.fault_severity_id,
        asset_type_fault_id: selected.id,
        fault: selected.fault,
        resolved: false,
        acknowledged: false,
        severity: selected.severity,
        color: selected.color,
        comments: selected.description,
        severity_level: selected.severity_level,
        dropdown_key: selected.dropdown_key,
      }],
    ]);
  }

  emitChanged = (_: number) => this.faults$.next([
    ... this.faults$.value,
  ]);

  onViewResolutionNotes(index: number): void {
    if (index < 0) {
      return;
    }

    const faults = this.faults$.value;

    if (index > faults.length) {
      return;
    }

    const fault = faults[index];

    return this._dialog.show({
      component: AssetFaultViewResolutionNotesDialogComponent,
      data: {
        fault,
      },
      afterClosing: (result) => {
        if (blank(result)) {
          return;
        }

        fault.resolution_notes = result.notes;

        faults[index] = fault;

        this.faults$.next(faults);
      },
    });
  }

  onAcknowledgedAll(checked: boolean): void {
    const faults = [... this.faults$.value];

    for (const index in faults) {
      faults[index] = {
        ...faults[index],
        acknowledged: checked,
      };
    }

    this.faults$.next(faults);
  }

  onResolvedAll(checked: boolean): void {
    const faults = [... this.faults$.value];

    for (const index in faults) {
      faults[index] = {
        ...faults[index],
        resolved: checked,
      };
    }

    this.faults$.next(faults);
  }

  onSeverityChange(index: number, selected: AssetSeverityLevelOption): void {
    if (index < 0) {
      return;
    }

    const faults = [... this.faults$.value];

    if (faults.length < index) {
      return;
    }

    faults[index] = {
      ... faults[index],
      color: selected.color,
      dropdown_key: selected.dropdown_key,
      severity: selected.text,
      severity_level: selected.position,
    };

    this.faults$.next(faults);
  }

  onImageRemove(index: number): void {
    const current = this.faults$.value;

    if (current.length < index) {
      return;
    }

    if (index > current.length) {
      return;
    }

    current[index] = {
      ...current[index],
      image_path: null,
      image_url: null,
    };

    this.faults$.next(current);
  }

  private _listenToFieldChanges(): void {
    const tabs: StudioFormTab[] = fallback(this._forms.tabs, {
      fallback: () => [],
    });

    const info = get_studio_fields_info(tabs, {
      field_names: ['asset_type_id'],
    });

    if (blank(info)) {
      return;
    }

    this._assetTypeId$.next(info[0].field.default_value);

    this._onAssetTypeChangeSubscription = info[0].form_control.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap((id) => this._assetTypeId$.next(id)),
      tap(() => this._fetchPossibilities()),
    ).subscribe();
  }

  private _fetchPossibilities = () => observe({
    observable: () => defer(() => {
      const id = this._assetTypeId$.value;

      if (blank(id)) {
        return of([]);
      }

      return this._api.callPrivate$({
        path: '/asset_types/fault_codes',
        action: 'post',
        expectsNewFormat: true,
        body: {
          'asset_type_id': this._assetTypeId$.value,
        },
        onSuccessResponse: (response) => arr_reduce<AssetSeverityFaultOption[], AssetTypeFaultSeverity>(response.get('items'), {
          initial_value: [],
          reducer: (acc, curr) => {
            const faults = curr.faults;

            if (blank(faults)) {
              return acc;
            }

            for (const fault of faults) {
              acc.push({
                id: fault.id,
                text: fault.fault,
                fault: fault.fault,
                fault_severity_id: curr.id,
                color: curr.color,
                groupName: curr.severity,
                severity: curr.severity,
                description: fault.description,
                severity_level: curr.severity_level,
                dropdown_key: curr.dropdown_key,
              });
            }

            return acc;
          },
        })
      });
    }),
  })
    .subscribe((posibilities) => this.possibilities$.next(posibilities));

  private _fetchGlobalSeverities(): void {
    this._onFetchingSeverityOptionsSubscription = this._api.callPrivate$({
      action: 'post',
      path: '/dropdown/field_options',
      body: {
        field: 'severity',
        module: 'asset_type_severities',
      },
      onSuccessResponse: (response) => map_to_arr(response),
    }).subscribe((options) => this.severities$.next(arr_map(options, {
      transformer: (option) => ({
        text: this._translator.instant(data_get(option, 'name')),
        dropdown_key: data_get(option, 'key'),
        color: data_get(option, 'color'),
        position: data_get(option, 'position'),
      }),
    })));
  }
}

type AssetActualFaultInputData = {
  id: string;
  asset_type_severity_id?: string;
  asset_type_fault_id?: string;
  fault: string;
  resolved?: boolean;
  quote_id?: string;
  acknowledged?: boolean;
  deleted?: boolean;
  severity?: string;
  color?: string;
  comments?: string;
  resolution_notes?: string;
  date_resolved?: string;
  severity_level?: number;
  dropdown_key?: string;
  image_path?: string;
  image_url?: string;
}

type AssetSeverityFaultOption = FieldmagicDropdownOption<{
  id: string;
  fault_severity_id: string;
  fault: string;
  color: string;
  severity: string;
  description?: string;
  severity_level: number;
  dropdown_key: string;
}>;

type AssetSeverityLevelOption = FieldmagicDropdownOption<{
  text: string;
  dropdown_key: string;
  color: string;
  position: number;
}>;

@Component({
  selector: 'asset-faults-view-resolution-notes-dialog',
  template: `
    <fieldmagic-dialog
      label="Resolution Notes"
      icon="file-alt"
      [instance]="this"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <fieldmagic-primary-button
          purpose="save"
          (click)="onSave()"
        >
        </fieldmagic-primary-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <div class="d-flex flex-column">
          <fieldmagic-textarea-input
            label="Resolution Notes"
            placeholder="Add notes..."
            [standalone]="false"
            [expandable]="true"
            [(ngModel)]="notes"
          >
          </fieldmagic-textarea-input>

          <small
            *ngIf="resolutionDate | filled"
            class="text-muted"
          >
            {{ 'Date Resolved: %s' | translate | sprintf: [resolutionDate] }}
          </small>
        </div>
      </ng-template>
    </fieldmagic-dialog>
  `,
})
export class AssetFaultViewResolutionNotesDialogComponent implements OnInit {
  notes?: OptionalValue<string>;
  resolutionDate?: OptionalValue<string>;

  constructor(
    @Inject(DIALOG_DATA) private readonly _data: AssetFaultViewResolutionNotesDialogData,
    private readonly _dialog: DialogService,
  ) { }

  ngOnInit(): void {
    this.notes = this._data.fault.resolution_notes;
    this.resolutionDate = transform(data_get(this._data.fault, 'date_resolved'), {
      transformer: (value) => format_datetime(to_local_datetime(value), {
        format: 'll',
      }),
    });
  }

  onSave = () => this._dialog.close<this, AssetFaultViewResolutionNotesDialogResult>({
    instance: this,
    result: {
      notes: this.notes,
    },
  });
}

type AssetFaultViewResolutionNotesDialogData = {
  fault: AssetActualFaultInputData;
};

type AssetFaultViewResolutionNotesDialogResult = OptionalValue<{
  notes: OptionalValue<string>;
}>;